import {
  Badge,
  Box,
  Button,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useEffect, useState } from "react";
import services from "../services";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { OrderStatus, Roles } from "../enums";

type PaymentRouteBadgeProps = {
  orderId: string;
  payableBy?: string;
  payableTo?: string;
  orderStatus?: string;
  onUpdated?: () => void;
};

const PAY_ROUTE_EDITABLE_STATUSES: string[] = [
  OrderStatus.NEW,
  OrderStatus.ACK,
  OrderStatus.ALLOCATED,
];

const payByOptions = ["CUSTOMER", "INSURANCE", "NBFC"];
const payToOptions = ["GARAAZ", "SUPPLIER"];

const resolveDisplayLabel = (value?: string) => {
  if (!value) return "-";
  return value.charAt(0) + value.slice(1).toLowerCase();
};

const PaymentRouteBadge: React.FC<PaymentRouteBadgeProps> = ({
  orderId,
  payableBy,
  payableTo,
  orderStatus,
  onUpdated,
}) => {
  const toast = useToast();
  const userDetails = useUserDetailsContext();
  const isUserHeadOfOperations =
    userDetails?.userSubType === Roles.HEAD_OF_OPERATIONS;
  const canEditPaymentRoute =
    orderStatus != null && PAY_ROUTE_EDITABLE_STATUSES.includes(orderStatus);
  const [committedPayBy, setCommittedPayBy] = useState(payableBy ?? "");
  const [committedPayTo, setCommittedPayTo] = useState(payableTo ?? "");
  const [selectedPayBy, setSelectedPayBy] = useState(payableBy ?? "");
  const [selectedPayTo, setSelectedPayTo] = useState(payableTo ?? "");
  const [isEditingPayBy, setIsEditingPayBy] = useState(false);
  const [isEditingPayTo, setIsEditingPayTo] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges =
    (selectedPayBy || "") !== (committedPayBy || "") ||
    (selectedPayTo || "") !== (committedPayTo || "");

  useEffect(() => {
    const nextPayBy = payableBy ?? "";
    const nextPayTo = payableTo ?? "";
    setCommittedPayBy(nextPayBy);
    setCommittedPayTo(nextPayTo);
    setSelectedPayBy(nextPayBy);
    setSelectedPayTo(nextPayTo);
  }, [payableBy, payableTo]);

  const handleCancel = () => {
    setSelectedPayBy(committedPayBy);
    setSelectedPayTo(committedPayTo);
    setIsEditingPayBy(false);
    setIsEditingPayTo(false);
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    try {
      setIsSaving(true);
      await services.updateOrder(orderId, {
        payableBy: selectedPayBy,
        payableTo: selectedPayTo,
      });
      setCommittedPayBy(selectedPayBy);
      setCommittedPayTo(selectedPayTo);
      setIsEditingPayBy(false);
      setIsEditingPayTo(false);
      onUpdated?.();
      toast({
        description: "Payment route updated",
        status: "success",
        duration: 2000,
        position: "top",
        isClosable: true,
      });
    } catch (error: unknown) {
      toast({
        description: getApiErrorMessage(
          error,
          "Failed to update payment route",
        ),
        status: "error",
        duration: 2500,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const payerLabel = resolveDisplayLabel(selectedPayBy || committedPayBy);
  const payeeLabel = resolveDisplayLabel(selectedPayTo || committedPayTo);

  return (
    <Popover placement="bottom-end" trigger="hover">
      <PopoverTrigger>
        <Box
          ml={2}
          px={1}
          py={0.5}
          border="1px solid #CBD5E0"
          borderRadius="8px"
          cursor="pointer"
          bg="#EDF2F7"
        >
          <HStack spacing={1.5} fontSize="2xs">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="#EEF2FF"
              borderRadius="4px"
              p={0.5}
            >
              <CurrencyRupeeIcon style={{ fontSize: 12, color: "#6366F1" }} />
            </Box>
            <Text fontWeight="bold" color="#1A202C">
              {committedPayBy || "-"}
            </Text>
            <ArrowForwardIcon style={{ fontSize: 12, color: "#718096" }} />
            <Text fontWeight="bold" color="#1A202C">
              {committedPayTo || "-"}
            </Text>
            <InfoOutlinedIcon style={{ fontSize: 12, color: "#718096" }} />
          </HStack>
        </Box>
      </PopoverTrigger>
      <PopoverContent
        bg="#FFFFFF"
        borderColor="#E2E8F0"
        borderRadius="14px"
        color="#374151"
        p={3}
        maxW="300px"
      >
        <Text
          textAlign="center"
          fontSize="md"
          color="#4B5563"
          fontWeight="700"
          letterSpacing="0.4px"
          mb={2}
        >
          Payment Route
        </Text>
        <HStack
          justifyContent="space-between"
          borderTop="1px solid #E5E7EB"
          py={1.5}
        >
          <Text fontSize="md" color="#374151">
            Pay By:
          </Text>
          <HStack spacing={1.5}>
            {isEditingPayBy ? (
              <Select
                size="xs"
                width="120px"
                value={selectedPayBy || committedPayBy || ""}
                onChange={(e) => {
                  setSelectedPayBy(e.target.value);
                }}
              >
                {payByOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            ) : (
              <Badge
                px={1.5}
                py={0.5}
                borderRadius="4px"
                fontSize="xs"
                bg="#E5E7EB"
                color="#374151"
              >
                {payerLabel}
              </Badge>
            )}
            {isUserHeadOfOperations &&
              !isEditingPayBy &&
              (canEditPaymentRoute ? (
                <Box
                  as="button"
                  type="button"
                  display="flex"
                  onClick={() => setIsEditingPayBy(true)}
                >
                  <EditOutlinedIcon
                    style={{ fontSize: 14, color: "#6B7280" }}
                  />
                </Box>
              ) : (
                <Tooltip
                  label="Pay By can't be changed after Ready to Dispatch"
                  shouldWrapChildren
                >
                  <Box
                    as="span"
                    display="inline-flex"
                    cursor="not-allowed"
                    opacity={0.45}
                  >
                    <EditOutlinedIcon
                      style={{ fontSize: 14, color: "#6B7280" }}
                    />
                  </Box>
                </Tooltip>
              ))}
          </HStack>
        </HStack>
        <HStack
          justifyContent="space-between"
          borderTop="1px solid #E5E7EB"
          py={1.5}
        >
          <Text fontSize="md" color="#374151">
            Pay To:
          </Text>
          <HStack spacing={1.5}>
            {isEditingPayTo ? (
              <Select
                size="xs"
                width="120px"
                value={selectedPayTo || committedPayTo || ""}
                onChange={(e) => {
                  setSelectedPayTo(e.target.value);
                }}
              >
                {payToOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            ) : (
              <Badge
                px={1.5}
                py={0.5}
                borderRadius="4px"
                fontSize="xs"
                bg="#E5E7EB"
                color="#374151"
              >
                {payeeLabel}
              </Badge>
            )}
            {isUserHeadOfOperations &&
              !isEditingPayTo &&
              (canEditPaymentRoute ? (
                <Box
                  as="button"
                  type="button"
                  display="flex"
                  onClick={() => setIsEditingPayTo(true)}
                >
                  <EditOutlinedIcon
                    style={{ fontSize: 14, color: "#6B7280" }}
                  />
                </Box>
              ) : (
                <Tooltip
                  label="Pay To can't be changed after Ready to Dispatch"
                  shouldWrapChildren
                >
                  <Box
                    as="span"
                    display="inline-flex"
                    cursor="not-allowed"
                    opacity={0.45}
                  >
                    <EditOutlinedIcon
                      style={{ fontSize: 14, color: "#6B7280" }}
                    />
                  </Box>
                </Tooltip>
              ))}
          </HStack>
        </HStack>
        {(isEditingPayBy || isEditingPayTo) && (
          <Box width="100%" pt={1.5}>
            <HStack width="100%" justifyContent="center" spacing={2}>
              <Button
                size="xs"
                variant="ghost"
                onClick={handleCancel}
                flex={1}
                minW={0}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={!hasChanges}
                flex={1}
                minW={0}
              >
                Save Changes
              </Button>
            </HStack>
          </Box>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default PaymentRouteBadge;
